import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Calendar, 
  MapPin, 
  Users, 
  Clock, 
  Video, 
  Coffee,
  Sparkles,
  Filter,
  Search,
  ChevronRight,
  Mail
} from 'lucide-react';
import { apiGet } from '../lib/api';
import SEO from '../components/SEO';

const EVENT_TYPE_CONFIG = {
  meetup: { label: 'Meetup', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300', icon: Users },
  workshop: { label: 'Workshop', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300', icon: Sparkles },
  webinar: { label: 'Webinar', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300', icon: Video },
  networking: { label: 'Networking', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300', icon: Users },
  fika: { label: 'Fika', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300', icon: Coffee },
};

export default function Events() {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await apiGet('/events', { status: 'upcoming', limit: 50 });
        const eventsList = res?.data?.events || res?.events || [];
        setEvents(eventsList);
      } catch (err) {
        setError(err?.response?.data?.error || err?.message || 'Failed to load events');
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  // Filter events
  const filteredEvents = events.filter(event => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm ||
      (event.title || '').toLowerCase().includes(searchLower) ||
      (event.description || '').toLowerCase().includes(searchLower) ||
      (event.location || event.venue_name || '').toLowerCase().includes(searchLower);

    const matchesType = typeFilter === 'all' || event.event_type === typeFilter;

    // Check for online events based on venue_name containing "online" or is_online flag
    const isOnline = event.is_online || 
      (event.venue_name || '').toLowerCase().includes('online') ||
      (event.location || '').toLowerCase().includes('online');

    const matchesLocation = locationFilter === 'all' ||
      (locationFilter === 'online' && isOnline) ||
      (locationFilter === 'in_person' && !isOnline);

    // Only show future events
    const eventDate = event.starts_at || event.start_at;
    const isFuture = eventDate ? new Date(eventDate) > new Date() : true;

    return matchesSearch && matchesType && matchesLocation && isFuture;
  });

  // Sort by date
  const sortedEvents = [...filteredEvents].sort((a, b) => {
    const dateA = a.starts_at || a.start_at;
    const dateB = b.starts_at || b.start_at;
    return new Date(dateA) - new Date(dateB);
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (startStr, endStr) => {
    if (!startStr) return '';
    const start = new Date(startStr);
    const startTime = start.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    
    if (endStr) {
      const end = new Date(endStr);
      const endTime = end.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
      return `${startTime} - ${endTime}`;
    }
    return startTime;
  };

  const getDaysUntil = (dateStr) => {
    if (!dateStr) return null;
    const eventDate = new Date(dateStr);
    const today = new Date();
    const diffTime = eventDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays < 7) return `In ${diffDays} days`;
    if (diffDays < 14) return 'Next week';
    return `In ${Math.ceil(diffDays / 7)} weeks`;
  };

  const getEventTypeConfig = (type) => {
    return EVENT_TYPE_CONFIG[type] || { label: 'Event', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300', icon: Calendar };
  };

  const getEventLocation = (event) => {
    return event.location || event.venue_name || event.venue_address || 'Location TBA';
  };

  const isEventOnline = (event) => {
    return event.is_online || 
      (event.venue_name || '').toLowerCase().includes('online') ||
      (event.location || '').toLowerCase().includes('online');
  };

  return (
    <>
      <SEO 
        title="Events - Kreatives"
        description="Join free creative events, workshops, meetups, and webinars. Connect with fellow creatives across the Nordics."
      />

      <div className="min-h-screen flex flex-col">
        <main className="flex-1 divide-y divide-zinc-200 dark:divide-zinc-800 space-y-8">
          {/* Hero Section - matching About page style */}
          <section className="py-6 px-4">
            <div className="container">
              <div className="relative h-[30vh] min-h-[250px] overflow-hidden flex items-center justify-center">
                <div className="text-center px-4">
                  <h1 className="text-4xl md:text-5xl font-bold mb-4 text-primary-text">
                    Creative Events
                  </h1>
                  <p className="text-xl max-w-2xl mx-auto text-secondary-text">
                    Join workshops, meetups, and webinars to learn, network, and grow with fellow creatives across the Nordics.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Filters Section */}
          <section className="py-6 px-4">
            <div className="container mx-auto max-w-6xl">
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Search */}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400" />
                  <input
                    type="text"
                    placeholder="Search events..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-card-bg border border-card-border rounded-xl text-primary-text placeholder:text-zinc-400 focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                </div>

                {/* Type Filter */}
                <div className="flex items-center gap-2">
                  <Filter className="h-5 w-5 text-secondary-text" />
                  <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    className="px-4 py-3 bg-card-bg border border-card-border rounded-xl text-primary-text focus:border-primary focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="all">All Types</option>
                    <option value="meetup">Meetups</option>
                    <option value="workshop">Workshops</option>
                    <option value="webinar">Webinars</option>
                    <option value="networking">Networking</option>
                    <option value="fika">Fika Sessions</option>
                  </select>
                </div>

                {/* Location Filter */}
                <select
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  className="px-4 py-3 bg-card-bg border border-card-border rounded-xl text-primary-text focus:border-primary focus:ring-2 focus:ring-primary/20"
                >
                  <option value="all">All Locations</option>
                  <option value="online">Online Only</option>
                  <option value="in_person">In Person</option>
                </select>
              </div>

              {/* Results Count */}
              <div className="mt-6">
                <p className="text-secondary-text">
                  {sortedEvents.length} upcoming event{sortedEvents.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </section>

          {/* Events Grid Section */}
          <section className="py-10 px-4">
            <div className="container mx-auto max-w-6xl">
              {/* Loading */}
              {loading && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[1, 2, 3, 4, 5, 6].map(i => (
                    <div key={i} className="bg-card-bg rounded-2xl overflow-hidden border border-card-border animate-pulse">
                      <div className="h-48 bg-subtle-bg" />
                      <div className="p-6 space-y-4">
                        <div className="h-6 bg-subtle-bg rounded w-3/4" />
                        <div className="h-4 bg-subtle-bg rounded w-full" />
                        <div className="h-4 bg-subtle-bg rounded w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="text-center py-12">
                  <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 px-6 py-4 rounded-xl inline-block">
                    {error}
                  </div>
                </div>
              )}

              {/* Empty State */}
              {!loading && !error && sortedEvents.length === 0 && (
                <div className="text-center py-16 bg-card-bg rounded-2xl border border-card-border">
                  <Calendar className="w-16 h-16 text-zinc-300 dark:text-zinc-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-primary-text mb-2">
                    No upcoming events
                  </h3>
                  <p className="text-secondary-text mb-6">
                    {searchTerm || typeFilter !== 'all' || locationFilter !== 'all'
                      ? 'Try adjusting your filters'
                      : 'Check back soon for new events!'}
                  </p>
                  {(searchTerm || typeFilter !== 'all' || locationFilter !== 'all') && (
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setTypeFilter('all');
                        setLocationFilter('all');
                      }}
                      className="btn btn-secondary"
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              )}

              {/* Events Grid */}
              {!loading && sortedEvents.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {sortedEvents.map(event => {
                    const typeConfig = getEventTypeConfig(event.event_type);
                    const TypeIcon = typeConfig.icon;
                    const eventDate = event.starts_at || event.start_at;
                    const eventEndDate = event.ends_at || event.end_at;
                    const daysUntil = getDaysUntil(eventDate);
                    const eventLocation = getEventLocation(event);
                    const isOnline = isEventOnline(event);

                    return (
                      <Link
                        key={event.id}
                        to={`/events/${event.id}`}
                        className="group bg-card-bg rounded-2xl overflow-hidden border border-card-border hover:border-primary/50 hover:shadow-xl transition-all duration-300 card-hover"
                      >
                        {/* Image */}
                        <div className="relative h-48 overflow-hidden">
                          {event.image_url ? (
                            <img
                              src={event.image_url}
                              alt={event.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-primary/20 to-purple-500/20 flex items-center justify-center">
                              <TypeIcon className="w-16 h-16 text-primary/50" />
                            </div>
                          )}
                          
                          {/* Badges */}
                          <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${typeConfig.color}`}>
                              <TypeIcon className="w-3 h-3" />
                              {typeConfig.label}
                            </span>
                            {isOnline && (
                              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                                <Video className="w-3 h-3" />
                                Online
                              </span>
                            )}
                          </div>

                          {/* Free Badge */}
                          {event.is_free && (
                            <div className="absolute top-4 right-4">
                              <span className="px-3 py-1 rounded-full text-xs font-bold bg-emerald-500 text-white">
                                FREE
                              </span>
                            </div>
                          )}

                          {/* Days Until */}
                          {daysUntil && (
                            <div className="absolute bottom-4 right-4">
                              <span className="px-3 py-1 rounded-full text-xs font-medium bg-black/60 text-white backdrop-blur-sm">
                                {daysUntil}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Content */}
                        <div className="p-6">
                          <h3 className="text-lg font-semibold text-primary-text mb-3 group-hover:text-primary transition-colors line-clamp-2">
                            {event.title}
                          </h3>

                          <div className="space-y-2 text-sm text-secondary-text">
                            {/* Date & Time */}
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 flex-shrink-0" />
                              <span>{formatDate(eventDate)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 flex-shrink-0" />
                              <span>{formatTime(eventDate, eventEndDate)}</span>
                            </div>

                            {/* Location */}
                            <div className="flex items-center gap-2">
                              {isOnline ? (
                                <Video className="w-4 h-4 flex-shrink-0" />
                              ) : (
                                <MapPin className="w-4 h-4 flex-shrink-0" />
                              )}
                              <span className="truncate">{eventLocation}</span>
                            </div>

                            {/* Capacity */}
                            {(event.capacity || event.max_attendees) && (
                              <div className="flex items-center gap-2">
                                <Users className="w-4 h-4 flex-shrink-0" />
                                <span>
                                  {event.registration_count || 0} / {event.capacity || event.max_attendees} registered
                                </span>
                              </div>
                            )}
                          </div>

                          {/* CTA */}
                          <div className="mt-4 pt-4 border-t border-card-border flex items-center justify-between">
                            <span className="text-sm font-medium text-primary group-hover:underline">
                              View Details
                            </span>
                            <ChevronRight className="w-5 h-5 text-primary group-hover:translate-x-1 transition-transform" />
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          {/* Host Event CTA Section - matching About page style */}
          <section className="py-10 px-4">
            <div className="container mx-auto max-w-4xl">
              <div className="text-center p-8">
                <h2 className="text-3xl font-bold mb-4 text-primary-text">
                  Want to host an event?
                </h2>
                <p className="text-xl text-secondary-text mb-8 max-w-2xl mx-auto">
                  Are you a creative professional who wants to share your knowledge? We'd love to help you host workshops, meetups, or webinars for our community.
                </p>
                <Link
                  to="/contact"
                  className="cta-brand inline-flex items-center justify-center gap-2 px-6 py-3"
                  data-variant="primary"
                >
                  <Mail className="w-5 h-5" aria-hidden="true" />
                  Get in Touch
                </Link>
              </div>
            </div>
          </section>
        </main>
      </div>
    </>
  );
}
